import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import {toastActions} from './toast'
import {roomDesignActions} from './roomDesign'
import {drawPreviewImage as doDrawPreviewImage} from '../libs/edit_after_photo'
import {generateAIEraserImage} from '../libs/aiEraserUtils'
import {AIEraserService} from '../api/aiEraser'

export interface Line {
    head: 'circle' | 'triangle' | 'square'
    headSize: number
    points: number[]
    isAIEraser?: boolean // AI消しゴムフラグ
}

export interface ExecuteAIEraserPayload {
    stage: any
    beforeImageRef: HTMLImageElement
    afterImageRef: HTMLImageElement
    aiEraserLines: Line[]
    currentImageId: string
}

const DefaultHeadSize = 50

const MAX_HISTORY_SIZE = 50

const initialState = {
    lines: [] as Line[],
    curHead: 'circle' as 'circle' | 'triangle' | 'square',
    curHeadSize: DefaultHeadSize,
    stage: undefined as any,
    outputStage: undefined as any,
    previewCanvas: undefined as any,
    watermarkLayerRef: null as HTMLImageElement | null,
    // 履歴管理（初期履歴のimageRefはnullで、初期画像を示す）
    historyStack: [{lines: [], aiEraserLines: [], imageRef: null}] as any[],
    historyIndex: 0,
    // ウォーターマーク表示設定（編集画面用）
    showWatermarkInEditor: true, // デフォルトは表示
    // AI消しゴム関連
    isAIEraserMode: false, // AI消しゴムモード
    aiEraserUsageByImage: {} as Record<string, number>, // 画像ごとの使用回数
    currentImageId: null as string | null, // 現在編集中の画像ID
    aiEraserMaxUsage: 3, // AI消しゴムの最大使用回数（1画像あたり）
    showAIEraserConfirm: false, // 実行/キャンセルポップアップ表示
    isAIEraserProcessing: false, // AI消しゴム処理中フラグ
    tempAIEraserLines: [] as Line[], // 一時的なAI消しゴムの線
    aiEraserLines: [] as Line[], // 確定したAI消しゴムの線（赤色表示用）
    // 画像履歴管理（AI消しゴム用）
    imageHistoryStack: [] as string[], // 画像のDataURL配列（最大4枚：初期画像＋AI消しゴム3回）
    initialImageRef: null as string | null, // 初期画像の参照（編集開始時の画像）
}

const drawPreviewImage = createAsyncThunk(
    'drawPreviewImage',
    async ({mouseX, mouseY}: any, thunkAPI: any) => {
        const {stage, previewCanvas} = thunkAPI.getState().editPhoto
        // ウォーターマークはオーバーレイStageのWatermarkLayerで表示するため、ここではnullを渡す
        doDrawPreviewImage(stage, previewCanvas, mouseX, mouseY, null)
    }
)

export const executeAIEraser = createAsyncThunk(
    'editPhoto/executeAIEraser',
    async (payload: ExecuteAIEraserPayload, thunkAPI: any) => {
        try {
            // UIフラグ更新
            thunkAPI.dispatch(editPhotoSlice.actions.setShowAIEraserConfirm(false))
            thunkAPI.dispatch(editPhotoSlice.actions.setIsAIEraserProcessing(true))

            // マスク画像生成（編集後の画像を使用）
            const maskedImage = await generateAIEraserImage(
                payload.stage,
                payload.afterImageRef,
                payload.aiEraserLines
            )

            // File変換（ファイル名生成ロジック）
            const timestamp = Date.now()
            const isDataURL = payload.currentImageId?.startsWith('data:')
            const stubId = isDataURL
                ? `ai_eraser_${timestamp}`
                : payload.currentImageId || `ai_eraser_${timestamp}`
            const filePrefix = isDataURL ? 'ai' : payload.currentImageId?.slice(0, 8) || 'ai'

            const maskedFile = new File(
                [maskedImage],
                `mask_${filePrefix}_${timestamp}.jpg`,
                { type: 'image/jpeg' }
            )

            // API送信
            const response = await AIEraserService.processAIEraser({
                formData: {
                    masked_image: maskedFile,
                    stub_id: stubId
                }
            })

            // エラーチェック
            if (!response.data.success) {
                throw new Error(response.data.message || 'AI消しゴム処理に失敗しました')
            }

            // 使用回数更新
            if (response.data.usage_count !== undefined && payload.currentImageId) {
                thunkAPI.dispatch(editPhotoSlice.actions.setAIEraserUsageForImage({
                    imageId: payload.currentImageId,
                    usageCount: response.data.usage_count
                }))
            }

            // 画像読み込み（Promiseでラップして同期的に扱う）
            const processedImage = await new Promise<HTMLImageElement>((resolve, reject) => {
                const img = new Image()
                img.crossOrigin = 'anonymous' // CORS対応
                img.onload = () => resolve(img)
                img.onerror = () => reject(new Error('画像の読み込みに失敗しました'))
                img.src = response.data.result_image_url!
            })

            // 画像更新
            thunkAPI.dispatch(roomDesignActions.setAfterImageRef(processedImage))

            // AI消しゴムの線をクリア
            thunkAPI.dispatch(editPhotoSlice.actions.clearAIEraserLines())

            // 履歴スタックをクリアして、処理後の画像を初期状態として設定
            thunkAPI.dispatch(editPhotoSlice.actions.clearHistoryAndSetInitialImage({
                imageRef: processedImage
            }))

            // プレビュー再描画（遅延実行）
            setTimeout(() => {
                thunkAPI.dispatch(drawPreviewImage({}))
            }, 100)

            // 成功メッセージ
            const remainingCount = response.data.remaining_count
            const message = remainingCount !== undefined
                ? `AI消しゴム処理が完了しました（残り${remainingCount}回）`
                : response.data.message || 'AI消しゴム処理が完了しました'
            thunkAPI.dispatch(toastActions.toastSuccess(message))

            // 処理完了フラグ
            thunkAPI.dispatch(editPhotoSlice.actions.setIsAIEraserProcessing(false))

            return { success: true, remainingCount }

        } catch (error: any) {
            // エラーハンドリング
            thunkAPI.dispatch(editPhotoSlice.actions.setIsAIEraserProcessing(false))
            const errorMessage = error.message || '通信エラーが発生しました'
            thunkAPI.dispatch(toastActions.toastError(errorMessage))
            throw error
        }
    }
)

const editPhotoSlice = createSlice({
    name: 'editPhoto',
    initialState,
    reducers: {
        setDefaultCurHead: (state, action) => {
            state.curHead = action.payload
            state.curHeadSize = DefaultHeadSize
        },
        setCurHead: (state, action) => {
            state.curHead = action.payload
        },
        setCurHeadSize: (state, action) => {
            state.curHeadSize = action.payload
        },
        setStage: (state, action) => {
            state.stage = action.payload
        },
        setOutputStage: (state, action) => {
            state.outputStage = action.payload
        },
        setPreviewCanvas: (state, action) => {
            state.previewCanvas = action.payload
        },
        setLines: (state, action) => {
            state.lines = action.payload
        },
        addPoinToLastLine: (state, action) => {
            const {x, y} = action.payload
            let lastLine = state.lines[state.lines.length - 1];
            // add point
            lastLine.points = lastLine.points.concat([x, y]);
            state.lines.splice(state.lines.length - 1, 1, lastLine);
        },
        addPointsToLastLine: (state, action) => {
            // 複数のポイントを一度に追加（補間ポイント用）
            const points = action.payload as number[]
            let lastLine = state.lines[state.lines.length - 1];
            if (lastLine && points.length > 0) {
                lastLine.points = lastLine.points.concat(points);
                state.lines.splice(state.lines.length - 1, 1, lastLine);
            }
        },
        setInitState: (state, action) => {
            // forceResetフラグがある場合、または stage がない場合は強制リセット
            const forceReset = action?.payload?.forceReset
            const isReopening = forceReset || !state.stage || !state.outputStage || !state.previewCanvas


            if (isReopening) {
                // 初回またはポップアップを再度開く時：stageをリセット
                return {
                    ...initialState,
                    aiEraserUsageByImage: state.aiEraserUsageByImage // AI消しゴムの使用回数のみ保持
                }
            } else {
                // AI消しゴム実行中など：stage参照を保持
                return {
                    ...initialState,
                    stage: state.stage,              // 保持
                    outputStage: state.outputStage,  // 保持
                    previewCanvas: state.previewCanvas, // 保持
                    aiEraserUsageByImage: state.aiEraserUsageByImage // AI消しゴムの使用回数を保持
                }
            }
        },
        setWatermarkLayerRef: (state, action) => {
            state.watermarkLayerRef = action.payload
        },
        // 履歴管理アクション
        pushHistory: (state) => {
            // 現在の位置より後の履歴を削除（Undo後に新規描画した場合）
            if (state.historyIndex < state.historyStack.length - 1) {
                state.historyStack = state.historyStack.slice(0, state.historyIndex + 1)
            }

            // 新しい履歴を追加（通常の消しゴムとAI消しゴム、画像参照を保存）
            const newHistory = {
                lines: JSON.parse(JSON.stringify(state.lines)),
                aiEraserLines: JSON.parse(JSON.stringify(state.aiEraserLines)),
                imageRef: null // 画像はpushHistoryWithImageで設定
            }
            state.historyStack.push(newHistory)
            state.historyIndex = state.historyStack.length - 1

            // 履歴サイズ制限
            if (state.historyStack.length > MAX_HISTORY_SIZE) {
                state.historyStack.shift()
                state.historyIndex = state.historyStack.length - 1
            }
        },
        pushHistoryIfChanged: (state) => {
            // 現在の状態と最新の履歴を比較
            const currentState = {
                lines: state.lines,
                aiEraserLines: state.aiEraserLines
            }
            const currentStateJSON = JSON.stringify(currentState)
            const lastHistory = state.historyStack[state.historyIndex]
            // 互換性のため、古い履歴形式もサポート
            const lastHistoryState = Array.isArray(lastHistory)
                ? { lines: lastHistory, aiEraserLines: [], imageRef: null }
                : lastHistory
            const lastHistoryJSON = JSON.stringify({
                lines: lastHistoryState.lines,
                aiEraserLines: lastHistoryState.aiEraserLines
            })

            // 変更がある場合のみ履歴に追加
            if (currentStateJSON !== lastHistoryJSON) {
                // 現在の位置より後の履歴を削除（Undo後の新規操作時）
                if (state.historyIndex < state.historyStack.length - 1) {
                    state.historyStack = state.historyStack.slice(0, state.historyIndex + 1)
                }

                // 新しい履歴を追加
                const newHistory = {
                    lines: JSON.parse(JSON.stringify(state.lines)),
                    aiEraserLines: JSON.parse(JSON.stringify(state.aiEraserLines)),
                    imageRef: null // 画像はpushHistoryWithImageで設定
                }
                state.historyStack.push(newHistory)
                state.historyIndex = state.historyStack.length - 1

                // 履歴サイズ制限
                if (state.historyStack.length > MAX_HISTORY_SIZE) {
                    state.historyStack.shift()
                    state.historyIndex = state.historyStack.length - 1
                }
            }
        },
        undo: (state) => {
            if (state.historyIndex > 0) {
                state.historyIndex--
                const history = state.historyStack[state.historyIndex]

                // 互換性のため、古い履歴形式もサポート
                if (Array.isArray(history)) {
                    state.lines = JSON.parse(JSON.stringify(history))
                    state.aiEraserLines = []
                } else {
                    state.lines = JSON.parse(JSON.stringify(history.lines || []))
                    state.aiEraserLines = JSON.parse(JSON.stringify(history.aiEraserLines || []))
                }
                // 一時的なAI消しゴムの線をクリア
                state.tempAIEraserLines = []
            }
        },
        redo: (state) => {
            if (state.historyIndex < state.historyStack.length - 1) {
                state.historyIndex++
                const history = state.historyStack[state.historyIndex]
                // 互換性のため、古い履歴形式もサポート
                if (Array.isArray(history)) {
                    state.lines = JSON.parse(JSON.stringify(history))
                    state.aiEraserLines = []
                } else {
                    state.lines = JSON.parse(JSON.stringify(history.lines || []))
                    state.aiEraserLines = JSON.parse(JSON.stringify(history.aiEraserLines || []))
                }
                // 一時的なAI消しゴムの線をクリア
                state.tempAIEraserLines = []
            }
        },
        clearAll: (state) => {
            // すべてクリア（線と履歴を初期状態に戻す）
            state.lines = []
            state.aiEraserLines = []  // AI消しゴムの線もクリア
            state.historyStack = [{lines: [], aiEraserLines: [], imageRef: state.initialImageRef}]  // 初期画像を保持
            state.historyIndex = 0
        },
        toggleWatermarkInEditor: (state) => {
            state.showWatermarkInEditor = !state.showWatermarkInEditor
        },
        setShowWatermarkInEditor: (state, action) => {
            state.showWatermarkInEditor = action.payload
        },
        // AI消しゴム関連アクション
        toggleAIEraserMode: (state) => {
            state.isAIEraserMode = !state.isAIEraserMode
        },
        setAIEraserMode: (state, action) => {
            state.isAIEraserMode = action.payload
        },
        setCurrentImageId: (state, action) => {
            state.currentImageId = action.payload
            // 新しい画像の場合、使用回数を初期化
            if (action.payload && !state.aiEraserUsageByImage[action.payload]) {
                state.aiEraserUsageByImage[action.payload] = 0
            }
        },
        setAIEraserUsageForImage: (state, action) => {
            const { imageId, usageCount } = action.payload
            if (imageId && usageCount !== undefined) {
                state.aiEraserUsageByImage[imageId] = usageCount
            }
        },
        resetAIEraserUsageForImage: (state, action) => {
            const imageId = action.payload
            if (imageId && state.aiEraserUsageByImage[imageId]) {
                state.aiEraserUsageByImage[imageId] = 0
            }
        },
        setShowAIEraserConfirm: (state, action) => {
            state.showAIEraserConfirm = action.payload
        },
        setIsAIEraserProcessing: (state, action) => {
            state.isAIEraserProcessing = action.payload
        },
        setTempAIEraserLines: (state, action) => {
            state.tempAIEraserLines = action.payload
        },
        applyAIEraserLines: (state) => {
            // 一時的なAI消しゴムの線をAI消しゴム専用の配列に追加（通常のlinesには追加しない）
            state.aiEraserLines = [...state.aiEraserLines, ...state.tempAIEraserLines]
            state.tempAIEraserLines = []
            // 使用回数のインクリメントは実行ボタンを押した時のみ行う
        },
        clearTempAIEraserLines: (state) => {
            state.tempAIEraserLines = []
        },
        clearAIEraserLines: (state) => {
            state.aiEraserLines = []
        },
        // 画像履歴管理アクション
        setInitialImageRef: (state, action) => {
            state.initialImageRef = action.payload
            // 初期履歴にも画像を設定（nullまたはundefinedとして保存し、初期状態であることを示す）
            if (state.historyStack.length > 0) {
                // 初期履歴のimageRefはnullにして、初期画像を使うことを示す
                state.historyStack[0].imageRef = null
            }
        },
        pushHistoryWithImage: (state, action) => {
            const { imageRef } = action.payload
            // 現在の位置より後の履歴を削除
            if (state.historyIndex < state.historyStack.length - 1) {
                state.historyStack = state.historyStack.slice(0, state.historyIndex + 1)
            }

            // 新しい履歴を追加（画像参照付き）
            const newHistory = {
                lines: JSON.parse(JSON.stringify(state.lines)),
                aiEraserLines: JSON.parse(JSON.stringify(state.aiEraserLines)),
                imageRef: imageRef
            }
            state.historyStack.push(newHistory)
            state.historyIndex = state.historyStack.length - 1

            // 画像履歴スタックにも追加（最大4枚）
            if (!state.imageHistoryStack.includes(imageRef)) {
                state.imageHistoryStack.push(imageRef)
                if (state.imageHistoryStack.length > 4) {
                    state.imageHistoryStack.shift()
                }
            }

            // 履歴サイズ制限
            if (state.historyStack.length > MAX_HISTORY_SIZE) {
                state.historyStack.shift()
                state.historyIndex = state.historyStack.length - 1
            }
        },
        getCurrentImageRef: (state) => {
            const currentHistory = state.historyStack[state.historyIndex]
            if (currentHistory && currentHistory.imageRef) {
                return currentHistory.imageRef
            }
            return state.initialImageRef
        },
        // ポップアップクローズ時のクリーンアップ
        clearStageReferences: (state) => {
            state.stage = undefined
            state.outputStage = undefined
            state.previewCanvas = undefined
        },
        // AI消しゴム実行時の履歴クリア
        clearHistoryAndSetInitialImage: (state, action) => {
            const { imageRef } = action.payload
            // 履歴を完全にクリアして、新しい画像を初期状態とする
            state.lines = []
            state.aiEraserLines = []
            state.tempAIEraserLines = []
            state.historyStack = [{lines: [], aiEraserLines: [], imageRef: imageRef}]
            state.historyIndex = 0
            state.initialImageRef = imageRef
        },
    },
})

const editPhotoActions = {
    ...editPhotoSlice.actions,
    drawPreviewImage,
    executeAIEraser,
}
export {editPhotoActions}
export default editPhotoSlice.reducer